import React from 'react';
import { splitFormControlWrapperProps } from '@cmp-src/form/FormControlWrapper/helpers';
import { FormControlWrapper } from '@cmp-src/form/FormControlWrapper';
import {
  FilterableSelectProps,
  FilterableSelectPropsWithFormControl
} from './types';
import { Popover } from '@v2/components/overlay/Popover/Popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@v2/components/ui/command';
import { Button } from '@v2/components/ui/button';
import { cn, useDisclosure } from '@gettactic/helpers';
import { Check } from 'lucide-react';

export const FilterableSelectInput = React.forwardRef<
  HTMLInputElement,
  FilterableSelectProps
>((props, ref) => {
  const {
    trigger,
    onSelect,
    value,
    items,
    placeholder = 'Select',
    searchPlaceholder = 'Search...',
    triggerProps
  } = props;
  const comboboxDisclosure = useDisclosure();

  return (
    <Popover
      className={'p-0'}
      bodyProps={{ className: 'p-0' }}
      content={
        <Command
          filter={(value, searchTerm) => {
            return String(value).toLowerCase().includes(searchTerm) ? 1 : 0;
          }}
          value={value}
          className={'p-0'}
        >
          <CommandInput
            placeholder={searchPlaceholder}
            inputMask={props.mask}
            wrapperClassName={'border-0 shadow-none px-4'}
            inputClassName={'h-5 py-3 outline-none'}
            className={'relative border-b-1 border-b-neutral-100 p-0.5'}
          />
          <CommandList>
            <CommandEmpty>No items found.</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  onSelect={(currentValue) => {
                    onSelect?.(currentValue);
                    comboboxDisclosure.close();
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === item.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      }
      open={comboboxDisclosure.isOpen}
      onOpenChange={comboboxDisclosure.onOpenChange}
    >
      {trigger || (
        <Button
          variant="trigger"
          className={'w-full'}
          size={'trigger'}
          // biome-ignore lint/a11y/useSemanticElements: <explanation>
          role="combobox"
          aria-expanded={comboboxDisclosure.isOpen}
          {...triggerProps}
        >
          {value
            ? items.find((framework) => framework.value === value)?.label
            : placeholder}
        </Button>
      )}
    </Popover>
  );
});

export const FilterableSelect = React.forwardRef<
  HTMLInputElement,
  FilterableSelectPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <>
      <FormControlWrapper variant={'unstyled'} {...wrapperProps}>
        <FilterableSelectInput ref={ref} {...inputProps} />
      </FormControlWrapper>
    </>
  );
});

FilterableSelect.displayName = 'FilterableSelect';
